import Board from "@/components/Crossword/Board/Board";
import QuestionList from "@/components/Crossword/QuestionList/QuestionList";
import {useEffect, useMemo, useState} from "react";
import {cloneDeep} from "lodash";
import styles from './CrosswordView.module.scss'
import Dropdown from "@/components/UI/Dropdown/Dropdown";
import Icon from "@/components/UI/Icon/Icon";
import Keyboard from "@/components/Keyboard/Keyboard";
import Modal from "@/components/UI/Modal/Modal";
import {CrosswordDataType, crosswordDate} from "@/types/crosswords";
import {
  makeBlankBoard,
  createQuestionMaps,
  findNextOpen,
  crosswordStringToBoard,
  getFirstAvailableCell, isBoardComplete
} from "@/utils/crossword";
import QuestionBar from "@/components/Crossword/QuestionBar/QuestionBar";
import Button from "@/components/UI/Button/Button";
import KeybindsModal from "@/components/Crossword/KeybindsModal/KeybindsModal";
import StartOverlay from "@/components/StartOverlay/StartOverlay";
import {useCrosswordState} from "@/pages/crossword";
import {timeString} from "@/utils/time";
import EndModal from "@/components/Crossword/EndModal/EndModal";
import MVSlot from "@/components/Crossword/MVSlot/MVSlot";

type Props = {
  isAdmin?: boolean
  crossword: CrosswordDataType
}

const CrosswordView = ({crossword, isAdmin}: Props) => {

  const board = crosswordStringToBoard(crossword.grid)

  const [localUserBoard, setUserBoard] = useCrosswordState('crosswordUserBoard') // ,isAdmin ? board : makeBlankBoard(board))
  const userBoard = isAdmin ? board : localUserBoard
  const [selectedCell, setSelectedCell] = useState<{x: number, y: number} | undefined>(getFirstAvailableCell(userBoard))
  const [localCheckedBoard, setCheckedBoard] = useCrosswordState('crosswordCheckedBoard') // makeBlankBoard(board))
  const checkedBoard = isAdmin ? makeBlankBoard(board) : localCheckedBoard
  const [goingAcross, setGoingAcross] = useState(true)
  const [isAutocheckOn, setIsAutocheckOn] = useState(false)
  const [gameState, setGameState] = useCrosswordState('crosswordGameState') // ,'playing')
  const [timeElapsed, setTimeElapsed] = useCrosswordState('crosswordTimeElapsed') // , 0)
  const [pausedModalOpen, setPausedModalOpen] = useState(false)
  const [keybindsModalOpen, setKeybindsModalOpen] = useState(false)
  const [endModalOpen, setEndModalOpen] = useState(false)
  const [almostModalOpen, setAlmostModalOpen] = useState(false)
  const [fastestTime, setFastestTime] = useCrosswordState('crosswordFastestTime') // , 0)
  const [firstLoadModalOpen, setFirstLoadModalOpen] = useState(true)
  const [historicTimes, setHistoricTimes] = useCrosswordState('crosswordTimes')
  const [hasCheated, setHasCheated] = useCrosswordState('crosswordHasCheated')

  const paused = pausedModalOpen || keybindsModalOpen || firstLoadModalOpen

  const handleKeyPress = (key: string, shiftPressed?: boolean) => {
    if (!selectedCell) return
    if (gameState === 'success') return
    const isCorrect = checkedBoard[selectedCell.y][selectedCell.x] === '+'
    if (checkedBoard[selectedCell.y][selectedCell.x] === '-') {
      const newCheckedBoard = [...checkedBoard]
      newCheckedBoard[selectedCell.y][selectedCell.x] = ''
      setCheckedBoard(newCheckedBoard)
    }

    if (key.includes('Arrow')) {
      if (key === 'ArrowUp') handleCellSelect(selectedCell.y-1, selectedCell.x)
      if (key === 'ArrowDown') handleCellSelect(selectedCell.y+1, selectedCell.x)
      if (key === 'ArrowLeft') handleCellSelect(selectedCell.y, selectedCell.x-1)
      if (key === 'ArrowRight') handleCellSelect(selectedCell.y, selectedCell.x+1)
      return
    }

    if (key === 'Tab' || key === 'Enter') {
      handleNextQuestion(!!shiftPressed)
      return
    }

    if (key === ' ') {
      setGoingAcross(!goingAcross)
      return
    }

    if (isAdmin) return

    if (key === 'Backspace' || key === 'Delete') {
      if (userBoard[selectedCell.y][selectedCell.x] !== '.' && !isCorrect) setCell(selectedCell.y, selectedCell.x, '')
      if (key === 'Delete') return
      if (goingAcross) handleCellSelect(selectedCell.y, selectedCell.x-1)
      else handleCellSelect(selectedCell.y-1, selectedCell.x)
      return
    }

    const isSelectedCellEmpty = userBoard[selectedCell.y][selectedCell.x] === ''
    if (!isCorrect) setCell(selectedCell.y, selectedCell.x, key)
    const nextCell = findNextOpen(goingAcross, selectedCell, userBoard, isSelectedCellEmpty)
    if (nextCell) handleCellSelect(nextCell.y, nextCell.x)
    else handleNextQuestion(false)

  }

  const handleCellSelect = (i: number, j: number) => {
    if (i < 0 || i >= userBoard.length || j <0 || j >= userBoard[0].length) return;
    if (board[i][j] === '.' || board[i][j] === undefined) return
    if (i === selectedCell?.y && j === selectedCell?.x) setGoingAcross(!goingAcross)
    setSelectedCell({x: j, y: i})
  }

  const setCell = (i: number, j: number, letter: string) => {
    const newUserBoard = [...userBoard]
    newUserBoard[i][j] = letter
    setUserBoard(newUserBoard)
  }

  const handleQuestionClick = (across: boolean, question: string) => {
    const startOfQuestion = questionMaps.questionToCellMap[`${across ? 'A' : 'D'}${question}`]
    const nextOpen = findNextOpen(across, startOfQuestion, userBoard, true)
    if (nextOpen) handleCellSelect(nextOpen.y, nextOpen.x)
    else handleCellSelect(startOfQuestion.y, startOfQuestion.x)
    setGoingAcross(across)
  }

  const handleNextQuestion = (reverse: boolean, initialRecursionQuestion?: string, passedSelectedCell?: {x: number, y: number}, passedGoingAcross?: boolean) => {
    const sortedQuestionKeyList = Object.keys(questionMaps.questionToCellMap).sort((a,b) => a[0] === b[0] ? Number(a.slice(1)) > Number(b.slice(1)) ? 1 : -1 : a[0] === 'A' ? -1 : 1)

    let curSelectedCell = passedSelectedCell || selectedCell
    let curGoingAcross = passedGoingAcross !== undefined ? passedGoingAcross : goingAcross

    // No cell selected, so we just select the first question
    if (!curSelectedCell) {
      const firstQuestion = questionMaps.questionToCellMap[sortedQuestionKeyList[0]]
      handleCellSelect(firstQuestion.y, firstQuestion.x)
      setGoingAcross(sortedQuestionKeyList[0][0] === 'A')
      return
    }

    const currentQuestionKey = `${curGoingAcross ? 'A' : 'D'}${questionMaps.cellToQuestionMap[curSelectedCell.y][curSelectedCell.x][curGoingAcross ? 'across' : 'down']}`
    const currentQuestionIndex = sortedQuestionKeyList.indexOf(currentQuestionKey)

    if (currentQuestionIndex < 0) {
      console.error("Bad question key", currentQuestionKey)
      return
    }

    let nextQuestionIndex = currentQuestionIndex === sortedQuestionKeyList.length -1 ? 0 : currentQuestionIndex + 1
    if (reverse) nextQuestionIndex = currentQuestionIndex === 0 ? sortedQuestionKeyList.length -1 : currentQuestionIndex - 1

    const nextQuestionKey = sortedQuestionKeyList[nextQuestionIndex]
    let nextQuestion = questionMaps.questionToCellMap[nextQuestionKey]

    if (initialRecursionQuestion === nextQuestionKey) { // We've looped through all the questions and none are available, so we just set it to the first of the next question
      return
    }


    if (userBoard[nextQuestion.y][nextQuestion.x] !== '') {
      const nextSpace = findNextOpen(nextQuestionKey[0] === 'A', nextQuestion, userBoard, true)
      if (nextSpace) nextQuestion = nextSpace
      else if (!isBoardComplete(userBoard)) {
        handleNextQuestion(reverse, initialRecursionQuestion || nextQuestionKey, nextQuestion, nextQuestionKey[0] === 'A')
        return
      }
    }

    handleCellSelect(nextQuestion.y, nextQuestion.x)
    setGoingAcross(nextQuestionKey[0] === 'A')
  }

  const checkLetter = (x: number, y: number) => {
    if (!userBoard[y][x])  return ''
    if (userBoard[y][x] === board[y][x]) return  '+'
    else return '-'
  }
  const checkAndSetLetter = (x: number, y: number) => {
    setHasCheated(true)
    const check = checkLetter(x,y)
    const newCheckedBoard = [...checkedBoard]
    newCheckedBoard[y][x] = check
    setCheckedBoard(newCheckedBoard)
  }

  const checkWord = () => {
    if (!selectedCell) return
    setHasCheated(true)
    const newCheckedBoard = [...checkedBoard]
    if (goingAcross) {
      const questionNo = questionMaps.cellToQuestionMap[selectedCell.y][selectedCell.x].across
      questionMaps.cellToQuestionMap[selectedCell.y].forEach((cell, i) => {
        if (questionMaps.cellToQuestionMap[selectedCell.y][i].across === questionNo) newCheckedBoard[selectedCell.y][i] = checkLetter(i, selectedCell.y)
      })
    }
    else {
      const questionNo = questionMaps.cellToQuestionMap[selectedCell.y][selectedCell.x].down
      questionMaps.cellToQuestionMap.forEach((row, i) => {
        if (questionMaps.cellToQuestionMap[i][selectedCell.x].down === questionNo) newCheckedBoard[i][selectedCell.x] = checkLetter(selectedCell.x, i)
      })
    }
    setCheckedBoard(newCheckedBoard)
  }

  const autoCheck = () => {
    const newCheckedBoard = cloneDeep(checkedBoard)
    let isComplete = true
    let hasErrors = false
    for (let i = 0; i < board.length; i++) {
      for (let j = 0; j < board[0].length; j++) {
        if (board[i][j] === '.' || !userBoard[i][j]) {
          if (!userBoard[i][j]) isComplete = false
          continue
        }
        if (userBoard[i][j] === board[i][j]) newCheckedBoard[i][j] = '+'
        else {
          hasErrors = true
          newCheckedBoard[i][j] = '-'
        }
      }
    }
    return {
      isComplete,
      hasErrors,
      newCheckedBoard
    }
  }

  const checkBoard = () => {
    setHasCheated(true)
    setCheckedBoard(autoCheck().newCheckedBoard)
  }

  const revealCell = () => {
    if (!selectedCell) return
    setHasCheated(true)
    const newCheckedBoard = [...checkedBoard]
    newCheckedBoard[selectedCell.y][selectedCell.x] = '+'
    const newUserBoard = [...userBoard]
    newUserBoard[selectedCell.y][selectedCell.x] = board[selectedCell.y][selectedCell.x]
    setUserBoard(newUserBoard)
    setCheckedBoard(newCheckedBoard)
  }

  const questionMaps = useMemo(() => {
    return createQuestionMaps(board, crossword.acrossAnswers, crossword.downAnswers)
  }, [board])

  useEffect(() => {
    console.log('Autochecking board')
    const {isComplete, hasErrors, newCheckedBoard} = autoCheck()
    if (isAutocheckOn) {
      setHasCheated(true)
      setCheckedBoard(newCheckedBoard)
    }
    if (gameState === 'playing' && isComplete) {
      if (!hasErrors) {
        setGameState('success')
        const newHistoricTimes = {...historicTimes}
        newHistoricTimes[crossword.id] = {
          time: timeElapsed,
          date: crosswordDate(crossword.date)?.toISOString() || '',
          hasCheated: hasCheated
        }
        setHistoricTimes(newHistoricTimes)
        if (!fastestTime || (timeElapsed && (timeElapsed < (fastestTime || 0)))) {
          setFastestTime(timeElapsed || 0)
        }
        setEndModalOpen(true)
      }
      else {
        setAlmostModalOpen(true)
      }
    }

  }, [isAutocheckOn, userBoard]);

  useEffect(() => {
    if (gameState === 'success') {
      setEndModalOpen(true)
    }
  }, []);

  useEffect(() => {
    if (isAdmin || gameState !== 'playing') return
    const handleVisibilityChange = () => {
      if (document.hidden && !paused) {
        setPausedModalOpen(true)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAdmin, gameState, paused])

  useEffect(() => {
    if (paused || gameState === 'success' || isAdmin) return
    const timer = setInterval(() => {
      setTimeElapsed((timeElapsed || 0) + 1)
    }, 1000)
    return () => clearInterval(timer)
  }, [paused, gameState, timeElapsed])

  const timeElapsedString = useMemo(() => timeString(timeElapsed || 0), [timeElapsed])

  return (
    <>
      {!isAdmin &&
      <div className={styles.crosswordToolbar}>
        <div className={styles.timer}>
          {gameState === 'playing' ?
            <button className={styles.timeButton} onClick={() => setPausedModalOpen(true)}>
              {timeElapsedString}<span className={styles.pause}><Icon icon="pause"/></span>
            </button>
            :
            <Button onClick={() => setEndModalOpen(true)}>View my stats</Button>
          }

        </div>
        <KeybindsModal open={keybindsModalOpen} onStateChange={open => setKeybindsModalOpen(open)}/>
        <Dropdown
          direction="right"
          trigger={<a><Icon icon="lifeRing"/> Help</a>}
          entries={[
            {
              text: `Turn Autocheck ${isAutocheckOn ? 'Off' : 'On'}`,
              type: 'entry',
              onClick: () => setIsAutocheckOn(!isAutocheckOn)
            },
            {
              text: 'Check Letter',
              type: 'entry',
              onClick: () => selectedCell && checkAndSetLetter(selectedCell.x, selectedCell.y)
            },
            {
              text: 'Check Word',
              type: 'entry',
              onClick: checkWord
            },
            {
              text: 'Reveal Cell',
              type: 'entry',
              onClick: revealCell
            },
            {
              text: 'Check Puzzle',
              type: 'entry',
              onClick: checkBoard,
            }
          ]}
        />
      </div>
      }
      {!isAdmin &&
        <StartOverlay
          title="Crossword"
          description={crossword.description || ''}
          author={crossword.author || ''}
          dateString={crosswordDate(crossword.date)?.toLocaleDateString() || ''}
          onClose={() => setFirstLoadModalOpen(false)}
          subtitle={crossword.title}
        />
      }

      <div className={styles.middleContainer}>
        <div className={styles.leftContainer}>
          <Board
            goingAcross={goingAcross}
            selectedCell={selectedCell}
            board={board}
            userBoard={userBoard}
            checkedBoard={checkedBoard}
            onCellSelected={handleCellSelect}
            questionMap={questionMaps.cellToQuestionMap}
          />
          <MVSlot />
        </div>


        <QuestionList
          isAdmin={isAdmin}
          crossword={crossword}
          acrossQuestionMap={questionMaps.acrossQuestionMap}
          downQuestionMap={questionMaps.downQuestionMap}
          acrossWordMap={questionMaps.acrossWordMap}
          downWordMap={questionMaps.downWordMap}
          onCellSelected={handleCellSelect}
          selectedCell={selectedCell}
          cellToQuestionMap={questionMaps.cellToQuestionMap}
          onQuestionClick={handleQuestionClick}
          goingAcross={goingAcross}
        />
      </div>

      <MVSlot/>


      <QuestionBar
        onPreviousQuestion={() => handleNextQuestion(true)}
        onNextQuestion={() => handleNextQuestion(false)}
        selectedCell={selectedCell}
        goingAcross={goingAcross}
        accrossQuestionMap={questionMaps.acrossQuestionMap}
        downQuestionMap={questionMaps.downQuestionMap}
        cellToQuestionMap={questionMaps.cellToQuestionMap}
      />

      <Keyboard hidden={isAdmin} onKeyPressed={handleKeyPress} hideOnLargerScreens />

      {!isAdmin &&
        <EndModal open={endModalOpen} onClose={() => setEndModalOpen(false)} crossword={crossword} />
      }

      {!isAdmin &&
        <Modal onClose={() => setAlmostModalOpen(false)} open={almostModalOpen} header="You're almost there!">
          <h3>There are some errors in your puzzle, keep going!</h3>
        </Modal>
      }

      <Modal blur open={pausedModalOpen} size="small" header="Game Paused" footer={<Button onClick={() => setPausedModalOpen(false)}>Resume</Button>}>
        <p>Would you like to continue playing?</p>
      </Modal>


    </>
  )
}

export default CrosswordView